import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import analyticsService from '../services/analyticsService';
import { baseUrl } from '../config/environment';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user data from localStorage and verify session
  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('🔍 Dealer - Auth token:', token ? 'Present' : 'Missing');
      
      if (!token) {
        console.log('🔍 Dealer - No auth token, setting user to null');
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      // First, try to get user data from localStorage as a fallback
      const email = localStorage.getItem('email');
      const uid = localStorage.getItem('uid');
      const orgId = localStorage.getItem('orgId');
      const userName = localStorage.getItem('userName');
      const profileImageUrl = localStorage.getItem('profileImageUrl');
      const status = localStorage.getItem('status');
      
      console.log('🔍 Dealer - localStorage data:', { email, uid, orgId, userName, profileImageUrl });
      
      if (email && uid) {
        const userRole = localStorage.getItem('userRole') || 'orguserdealer';
        const fallbackUser = {
          email,
          uid,
          organization: orgId,
          name: userName || email.split('@')[0], // Use stored name or fallback to email
          profileImageUrl: profileImageUrl || null,
          role: userRole,
          status: status || 'NA'
        };
        setCurrentUser(fallbackUser);
        
        // Initialize analytics service with user data
        analyticsService.setUser({
          uid: fallbackUser.uid,
          email: fallbackUser.email,
          displayName: fallbackUser.name,
          roles: [userRole],
          organization: fallbackUser.organization,
          dealerId: fallbackUser.uid,
          status: fallbackUser.status
        });
        
        console.log('🔍 Dealer - Set fallback user from localStorage:', fallbackUser);
        console.log('🔍 Dealer - User name will be:', fallbackUser.name);
        console.log('🔍 Dealer - Profile image URL will be:', fallbackUser.profileImageUrl);
      } else {
        console.log('🔍 Dealer - No email or uid found in localStorage');
      }

      console.log('🔍 Dealer - Attempting to verify session...');
      console.log('🔍 Dealer - API base URL:', baseUrl);
      
      // Try to verify session and get updated user data
      try {
        const response = await apiService.verifySession();
        console.log('🔍 Dealer - API response:', response);
        console.log('🔍 Dealer - Response data:', response.data);
        console.log('🔍 Dealer - Response status:', response.status);
        
        if (response.data && response.data.user) {
          setCurrentUser(response.data.user);
          
          // Initialize analytics service with updated user data
          analyticsService.setUser({
            uid: response.data.user.uid,
            email: response.data.user.email,
            displayName: response.data.user.name || response.data.user.email,
            roles: response.data.user.roles || ['orguserdealer'],
            organization: response.data.user.organization,
            dealerId: response.data.user.uid,
            status: response.data.user.status || 'NA'
          });
          
          console.log('🔍 Dealer - Updated user data from API:', response.data.user);
          console.log('🔍 Dealer - Profile image URL:', response.data.user.profileImageUrl);
        }
        
        
       
      } catch (apiError) {
        console.error('🔍 Dealer - API call failed, using localStorage data:', apiError);
        // Keep the localStorage data that was already set
      }
      
    } catch (error) {
      console.error('🔍 Dealer - Error in fetchUserData:', error);
      
      // Final fallback to localStorage data
      const email = localStorage.getItem('email');
      const uid = localStorage.getItem('uid');
      const orgId = localStorage.getItem('orgId');
      const userName = localStorage.getItem('userName');
      const profileImageUrl = localStorage.getItem('profileImageUrl');
      const status = localStorage.getItem('status');
      
      if (email && uid) {
        const userRole = localStorage.getItem('userRole') || 'orguserdealer';
        const fallbackUser = {
          email,
          uid,
          organization: orgId,
          name: userName || email.split('@')[0], // Use stored name or fallback to email
          profileImageUrl: profileImageUrl || null,
          role: userRole,
          status: status || 'NA'
        };
        setCurrentUser(fallbackUser);
        console.log('🔍 Dealer - Set final fallback user:', fallbackUser);
      }
    } finally {
      setLoading(false);
      console.log('🔍 Dealer - fetchUserData completed, loading set to false');
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear ALL authentication-related tokens and data
      console.log('🧹 Clearing all authentication data from localStorage');
      localStorage.removeItem('authToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('otpToken');
      localStorage.removeItem('userSession');
      localStorage.removeItem('uid');
      localStorage.removeItem('email');
      localStorage.removeItem('orgId');
      localStorage.removeItem('userName');
      localStorage.removeItem('profileImageUrl');
      localStorage.removeItem('status');
      localStorage.removeItem('userRole');
      localStorage.removeItem('tempRegNumber');
      localStorage.removeItem('onboardingStatus');
      setCurrentUser(null);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchUserData();
    
    // Also check again after a short delay to catch any localStorage updates
    const timeoutId = setTimeout(() => {
      console.log('🔍 Dealer - Delayed user data check');
      fetchUserData();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  // Listen for localStorage changes (like after login)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === 'userName' || e.key === 'profileImageUrl') {
        console.log('🔍 Dealer - localStorage changed, refetching user data');
        fetchUserData();
      }
    };

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (for same-tab updates)
    const handleCustomStorageChange = () => {
      console.log('🔍 Dealer - Custom storage event, refetching user data');
      fetchUserData();
    };

    window.addEventListener('userDataUpdated', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleCustomStorageChange);
    };
  }, []);

  const value = {
    currentUser,
    loading,
    logout,
    fetchUserData,
    refreshUserData: fetchUserData // Alias for easier access
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
